import { Link, useLocation, Navigate } from 'react-router-dom';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import FAQSection from '../components/FAQSection';
import CTABand from '../components/CTABand';
import { Reveal, Chapter } from '../components/motion';
import { SITE, breadcrumbSchema, faqSchema, serviceSchema, localBusinessSchema } from '../data/site';
import { SERVICE_IMAGES_2 } from '../data/extraImages';
import { SERVICE_PAGES } from '../data/contentServices';

export default function ServicePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, '');
  const page = SERVICE_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  const path = `/${slug}`;
  const h1Lines = page.h1.length > 38
    ? [page.h1.split(' in ')[0], `in ${page.h1.split(' in ').slice(1).join(' in ')}`]
    : [page.h1];

  return (
    <>
      <SEO
        title={page.seoTitle}
        description={page.metaDescription}
        path={path}
        schemas={[
          localBusinessSchema,
          serviceSchema(page.serviceName, page.metaDescription, path),
          faqSchema(page.faqs),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: page.serviceName, path }]),
        ]}
      />

      <PageHero
        eyebrow={`${SITE.name} · ${page.eyebrow}`}
        h1Lines={h1Lines}
        sub={page.intro[0]}
        image={page.heroImage}
        imageAlt={page.heroAlt}
        applyUrl={page.applyUrl}
        crumbs={[{ name: page.serviceName }]}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <Chapter num="01" label="Overview" />
              {page.intro.slice(1).map((p, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{p}</p>
              ))}
            </Reveal>
            {SERVICE_IMAGES_2[slug] && (
              <Reveal delay={0.15}>
                <img
                  src={SERVICE_IMAGES_2[slug].src}
                  alt={SERVICE_IMAGES_2[slug].alt}
                  loading="lazy"
                  className="clip-frame w-full rounded-xl object-cover shadow-xl shadow-navy/15"
                  data-testid={`service-image2-${slug}`}
                />
              </Reveal>
            )}
          </div>

          <div className="mt-16 space-y-16">
            {page.sections.map((section, i) => (
              <Reveal key={section.h2}>
                <article className={`grid gap-8 lg:grid-cols-12 ${i % 2 === 1 ? '' : ''}`}>
                  <div className="lg:col-span-4">
                    <p className="font-mono text-sm font-bold text-brand">{String(i + 2).padStart(2, '0')}</p>
                    <h2 className="mt-2 font-display text-xl font-bold tracking-tight text-navy sm:text-2xl lg:text-3xl" data-testid={`section-h2-${i}`}>
                      {section.h2}
                    </h2>
                    <div className="mt-4 h-1 w-16 -skew-x-12 rounded bg-brand" />
                  </div>
                  <div className="lg:col-span-8">
                    {section.body.map((p, j) => (
                      <p key={j} className="text-base leading-relaxed text-slate-600 sm:text-lg">{p}</p>
                    ))}
                    {section.bullets && (
                      <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
                        {section.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm font-medium text-slate-700">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-trustgreen" /> {b}
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.link && (
                      <Link
                        to={section.link.path}
                        data-testid={`section-link-${i}`}
                        className="mt-5 inline-flex items-center gap-1 font-display text-sm font-bold text-brand transition-colors hover:text-brand-dark"
                      >
                        {section.link.label} <ChevronRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={page.faqs} heading={`${page.serviceName} FAQs`} chapterNum="03" idPrefix={`faq-${slug}`} />

      <CTABand
        heading={page.eyebrow === 'Financing' || page.eyebrow === 'Promotions' ? 'Talk With Our Team Today' : `Schedule ${page.serviceName} Today`}
        testid={`cta-${slug}`}
      />
    </>
  );
}
